<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ScenarioSelect from './ScenarioSelect.vue';
import MapSelect from './MapSelect.vue';

const { t } = useI18n();

interface TournamentRound {
  id: number;
  scenario_id: string;
  map_id?: string; // Optional: map selection is not required
}

interface Props {
  modelValue: TournamentRound[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  update: [value: TournamentRound[]];
}>();

const rounds = ref<TournamentRound[]>([]);

// Initialize with at least 3 rounds
const initializeRounds = () => {
  if (props.modelValue && props.modelValue.length >= 3) {
    rounds.value = [...props.modelValue];
  } else {
    rounds.value = [
      { id: 1, scenario_id: '', map_id: '' },
      { id: 2, scenario_id: '', map_id: '' },
      { id: 3, scenario_id: '', map_id: '' }
    ];
  }
};

// Watch for external changes
watch(() => props.modelValue, initializeRounds, { immediate: true });

const addRound = () => {
  const newId = Math.max(...rounds.value.map(r => r.id)) + 1;
  rounds.value.push({
    id: newId,
    scenario_id: '',
    map_id: ''
  });
  emit('update', rounds.value);
};

const removeRound = (roundId: number) => {
  if (rounds.value.length > 3) {
    rounds.value = rounds.value.filter(round => round.id !== roundId);
    emit('update', rounds.value);
  }
};

const updateRound = (roundId: number, field: 'scenario_id' | 'map_id', value: string) => {
  const round = rounds.value.find(r => r.id === roundId);
  if (round) {
    round[field] = value;
    emit('update', rounds.value);
  }
};
</script>

<template>
  <div class="tournament-rounds">
    <h4 class="rounds-title">{{ t('events.form.tournament_rounds.title') }}</h4>

    <div class="rounds-container">
      <div
        v-for="round in rounds"
        :key="round.id"
        class="round-item"
      >
        <div class="round-header">
          <h5 class="round-number">{{ t('events.form.tournament_rounds.round') }} {{ round.id }}</h5>
          <button
            v-if="round.id > 3"
            @click="removeRound(round.id)"
            class="remove-round-btn"
            type="button"
          >
            <i class="bi bi-dash-circle"></i>
          </button>
        </div>

        <div class="round-fields">
          <div class="field-group">
            <label class="field-label">{{ t('events.form.tournament_rounds.scenario') }}</label>
            <ScenarioSelect
              :model-value="round.scenario_id"
              @update:model-value="(value: string) => updateRound(round.id, 'scenario_id', value)"
            />
          </div>

          <div class="field-group">
            <label class="field-label">{{ t('events.form.tournament_rounds.map') }}</label>
            <MapSelect
              :model-value="round.map_id"
              @update:model-value="(value: string) => updateRound(round.id, 'map_id', value)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="add-round-container">
      <button
        @click="addRound"
        class="add-round-btn"
        type="button"
      >
        <i class="bi bi-plus-circle"></i>
        {{ t('events.form.tournament_rounds.add_round') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Color Variables
$primary-bg: #EFD8B0;
$primary-text: #BD9C65;
$icon-bg: #C29C58;
$icon-text: #60451F;
$hover-bg: #B08F4F;
$border-color: #BD9C65;
$focus-shadow: rgba(189, 156, 101, 0.2);

.tournament-rounds {
  margin-top: 20px;
  padding: 20px;
  background-color: rgba($primary-bg, 0.3);
  border: 1px solid $border-color;
  border-radius: 8px;
}

.rounds-title {
  margin: 0 0 20px 0;
  color: $primary-text;
  font-size: 18px;
  font-weight: 600;
}

.rounds-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.round-item {
  padding: 16px;
  background-color: $primary-bg;
  border: 1px solid $border-color;
  border-radius: 6px;
}

.round-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.round-number {
  margin: 0;
  color: $primary-text;
  font-size: 16px;
  font-weight: 600;
}

.remove-round-btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
    transform: scale(1.1);
  }
}

.round-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  color: $primary-text;
  font-weight: 500;
}

.add-round-container {
  margin-top: 20px;
  text-align: center;
}

.add-round-btn {
  background-color: $icon-bg;
  color: $icon-text;
  border: 1px solid $border-color;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: $hover-bg;
    transform: translateY(-1px);
  }

  .bi {
    font-size: 16px;
  }
}

// Responsive design
@media (max-width: 768px) {
  .round-fields {
    grid-template-columns: 1fr;
  }

  .tournament-rounds {
    padding: 16px;
  }

  .round-item {
    padding: 12px;
  }
}
</style>
